import { ButtonProps, TextProps } from "../../../custom";
import SlantSection from "../Slant/Slant";
import Button from "../Buttons/Button";
import { Flex } from "theme-ui";
import {
  GatsbyImage,
  IGatsbyImageData,
  StaticImage,
} from "gatsby-plugin-image";

interface SummarySectionProps {
  heading: TextProps;
  subText: TextProps;
  boldText?: TextProps;
  button1?: ButtonProps;
  button2?: ButtonProps;
  checklist?: string[];
  services: {
    heading: TextProps;
    services: string[];
  };
  bullets: string[];
  image: IGatsbyImageData;
  disclaimer: TextProps;
  className?: string;
  state?: string;
}

const SummarySection = ({
  heading,
  subText,
  button1,
  button2,
  services,
  image,
  checklist,
  bullets,
  boldText,
  disclaimer,
  className,
  state,
}: SummarySectionProps) => {
  return (
    <SlantSection
      background="white"
      variant="full"
      className={className}
      sx={{ justifyContent: "center" }}
    >
      <Flex
        sx={{
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          px: [3, null, null, 6],
          my: 4,
        }}
        data-sal={"slide-up"}
        data-sal-easing="easeOutQuart"
        data-sal-duration={1000}
        data-sal-delay="300"
      >
        {heading && (
          <h2
            sx={{
              color: heading.color,
              m: 0,
              sup: {
                fontSize: 20,
              },
            }}
            dangerouslySetInnerHTML={{ __html: heading.text }}
          />
        )}
        {subText && (
          <p
            sx={{
              color: subText.color,
              textAlign: "center",
              maxWidth: 1600,
              px: [4, null, null, null, 5],
              py: [3, null, null, null, 3],
            }}
            dangerouslySetInnerHTML={{ __html: subText.text }}
          />
        )}
        {boldText && (
          <p
            sx={{
              color: boldText.color,
              textAlign: "center",
              fontWeight: 700,
              maxWidth: 1600,
              px: [4, null, null, null, 5],
              py: [3, null, null, null, 0],
              m: 0,
            }}
            dangerouslySetInnerHTML={{ __html: boldText.text }}
          />
        )}
        {checklist && (
          <div
            sx={{
              mt: 3,
              columns: [1, null, 2, 4],
            }}
          >
            {checklist.map((check, i) => (
              <Flex
                key={i}
                sx={{
                  fontSize: [18, null, null, 20],
                  fontWeight: 600,
                  py: 2,
                  px: 3,
                  mx: 3,
                  flexDirection: ["column"],
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <StaticImage
                  width={70}
                  layout="fixed"
                  alt="Checkmark Icon"
                  src="../../images/icons/CheckMarkIcon.png"
                />
                <p
                  sx={{
                    color: "green",
                    textTransform: "uppercase",
                    fontWeight: 700,
                  }}
                >
                  {check}
                </p>
              </Flex>
            ))}
          </div>
        )}
        {bullets && (
          <div
            sx={{
              textAlign: "left",
              pb: [4, null, null, 5],
              px: 3,
            }}
          >
            {bullets.map((bullet, i) => (
              <li
                key={i}
                sx={{
                  fontSize: [18, null, null, 20],
                  fontWeight: 600,
                  py: 2,
                }}
              >
                {bullet}
              </li>
            ))}
          </div>
        )}
        {services && (
          <Flex
            sx={{
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <h2
              sx={{
                color: services.heading.color,
              }}
            >
              {services.heading.text}
            </h2>

            <Flex
              sx={{
                borderTop: "solid 2px #90CA45",
                borderBottom: "solid 2px #90CA45",
                mb: 3,
                py: 2,
                width: ["80%", null, null, "100%"],
                flexDirection: ["column", null, null, "row"],
                justifyContent: "center",
              }}
            >
              {services.services.map((s, i) => (
                <p
                  key={i}
                  sx={{
                    display: "flex",
                    flexDirection: ["column", null, null, "row"],
                    justifyContent: "center",
                    alignItems: "center",
                    m: 0,
                    p: 0,
                  }}
                >
                  {s}
                  {i < services.services.length - 1 && (
                    <span
                      sx={{
                        display: "block",
                        m: 2,
                        width: [30, null, null, 2],
                        height: [2, null, null, 30],
                        bg: "black",
                      }}
                    ></span>
                  )}
                </p>
              ))}
            </Flex>
          </Flex>
        )}
        <Flex
          sx={{
            flexWrap: "wrap",
            justifyContent: "center",
            my: 2,
          }}
        >
          {button1 && (
            <Button
              {...button1}
              sx={{
                mr: [0, button2 ? 5 : 0],
              }}
            />
          )}
          {button2 && <Button {...button2} />}
        </Flex>
        {image && (
          <GatsbyImage
            image={image}
            alt="image"
            sx={{
              my: [3, null, null, 4],
            }}
          />
        )}
        {disclaimer && state !== "Illinois" && (
          <p
            sx={{
              variant: "text.disclaimer",
              color: disclaimer.color,
              textAlign: "center",
              px: [4, null, null, null, 4],
              py: [3, null, null, 2],
              maxWidth: 1200,
            }}
            dangerouslySetInnerHTML={{ __html: disclaimer.text }}
          />
        )}
      </Flex>
    </SlantSection>
  );
};

export default SummarySection;
